/* ============================================================
   Repubblica Webflow homepage interactions
   Ported from https://repubblica-homepage-1.webflow.io/
   - Drag-to-scroll carousels (.ff-carousel-parent), pointer-based
     with grab cursor and click suppression after drag
   - Arrow paging ([data-rpw-prev]/[data-rpw-next] scroll the
     carousel referenced by data-rpw-target)
   - Scroll-scrubbed reveal ([data-rpw-reveal]) — port of the design's
     IX3 interaction i-9a90f9c0 / timeline t-7e91ff87: split headlines
     into words, each word opacity 0→1 / y 8px→0, 0.2s stagger on a
     2.5s timeline, progress scrubbed to scroll between "top 90%" and
     "top 40%" of the viewport (0.8s smoothing), so it plays forward
     scrolling down and reverses scrolling up
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Drag-to-scroll (ported from the design script) ----------
     Unlike the design's version, the pointer is captured only once the
     3px drag threshold is passed: capturing on pointerdown retargets
     pointerup to the container, which makes the browser dispatch the
     click there instead of on the card link — killing every card link
     for mouse users. */
  function initDragScroll(el) {
    if (el.dataset.ffInit) return;
    el.dataset.ffInit = '1';

    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.style.userSelect = 'none';

    var isDown = false, startX = 0, startScroll = 0, moved = false, activeId = null;

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      activeId = e.pointerId;
    });

    el.addEventListener('pointermove', function (e) {
      if (!isDown || e.pointerId !== activeId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 3) {
        moved = true;
        el.setPointerCapture(e.pointerId);
        el.style.cursor = 'grabbing';
      }
      if (moved) el.scrollLeft = startScroll - dx;
    });

    function endDrag() {
      if (!isDown) return;
      isDown = false;
      if (activeId !== null && el.hasPointerCapture(activeId)) {
        el.releasePointerCapture(activeId);
      }
      activeId = null;
      el.style.cursor = '';
    }
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    el.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    el.style.cursor = 'grab';

    // Enable/disable this carousel's prev/next arrows at the start/end (or when it can't scroll).
    // Done HERE (per-carousel) rather than in initArrow so AJAX-swapped recommendation / recently-
    // viewed carousels — which are re-run through initDragScroll after injection — always wire up
    // to the live element instead of a stale one captured when the arrow was first bound.
    var sec = el.closest('.shopify-section') || document;
    var arrows = Array.prototype.filter.call(
      sec.querySelectorAll('[data-rpw-prev], [data-rpw-next]'),
      function (btn) {
        var sel = btn.getAttribute('data-rpw-target');
        return (sel ? sec.querySelector(sel) : sec.querySelector('.ff-carousel-parent')) === el;
      }
    );
    if (arrows.length) {
      var updArrows = function () {
        arrows.forEach(function (btn) { updateArrow(btn, el, btn.hasAttribute('data-rpw-prev')); });
      };
      el.addEventListener('scroll', updArrows, { passive: true });
      window.addEventListener('resize', updArrows);
      updArrows();
      setTimeout(updArrows, 350);   // re-check once cards/images have settled the scroll width
    }
  }

  /* ---------- Arrow paging ---------- */
  function cardStep(carousel) {
    var track = carousel.querySelector('.ff-is-horizontal');
    if (!track || !track.children.length) return carousel.clientWidth * 0.8;
    var first = track.children[0];
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
    return first.getBoundingClientRect().width + gap;
  }

  // Disable a prev/next arrow when the carousel can't page further that way (at the start for
  // prev, at the end for next, or when there's nothing to scroll). Toggles .is-rpw-disabled +
  // aria-disabled; the CSS dims it and blocks pointer events.
  function updateArrow(btn, carousel, isPrev) {
    if (!carousel) return;
    var max = carousel.scrollWidth - carousel.clientWidth;
    var disabled = (max <= 1) ? true
      : isPrev ? (carousel.scrollLeft <= 1)
               : (carousel.scrollLeft >= max - 1);
    btn.classList.toggle('is-rpw-disabled', disabled);
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    if ('disabled' in btn) { try { btn.disabled = disabled; } catch (e) {} }
  }

  function initArrow(btn) {
    if (btn.dataset.rpwInit) return;
    btn.dataset.rpwInit = '1';
    btn.addEventListener('click', function () {
      // Resolve the carousel LAZILY on each click — recommendation carousels are AJAX-swapped
      // AFTER this arrow is bound, so a reference captured at init time would point at a stale,
      // detached element. (The disabled-state tracking lives in initDragScroll, which re-runs on
      // the freshly-injected carousel.)
      var root = btn.closest('.shopify-section') || document;
      var sel = btn.getAttribute('data-rpw-target');
      var carousel = sel ? root.querySelector(sel) : root.querySelector('.ff-carousel-parent');
      if (!carousel) return;
      var dir = btn.hasAttribute('data-rpw-prev') ? -1 : 1;
      carousel.scrollBy({ left: dir * cardStep(carousel), behavior: 'smooth' });
    });
  }

  /* ---------- Newsletter: submit without a page reload ----------
     The {% form 'customer' %} normally POSTs and the browser navigates to the
     302 redirect (?customer_posted=true) — a full reload, which is how the
     server renders "Thanks for subscribing!".

     We do NOT fetch() the endpoint: that drops the storefront spam-protection
     / reCAPTCHA token Shopify injects during a *native* submit, and it fails
     cross-origin under `shopify theme dev` (the form posts to the live
     domain). Instead we keep the native submit untouched and just point its
     target at a hidden iframe, so the response loads there and the visible
     page never navigates. The result is read back inline.

     The Liquid form.posted_successfully? block stays as the no-JS fallback:
     if this script fails, the form has no target and reloads as before. */
  var rpwNlSeq = 0;
  function initNewsletter(form) {
    if (form.dataset.rpwNlInit) return;
    form.dataset.rpwNlInit = '1';

    var button = form.querySelector('button[type="submit"]');
    var input = form.querySelector('input[type="email"]');
    var statusWrap = form.querySelector('.ff-newsletter-status');
    var statusText = statusWrap && statusWrap.querySelector('.ff-paragraph');
    var successMsg = (statusWrap && statusWrap.getAttribute('data-success')) ||
      'Thanks for subscribing!';
    var errorMsg = (statusWrap && statusWrap.getAttribute('data-error')) ||
      'Sorry, something went wrong. Please try again.';

    // Hidden iframe that catches the form response (keeps the page in place).
    var frame = document.createElement('iframe');
    frame.name = 'rpw-nl-' + (++rpwNlSeq);
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText =
      'position:absolute;left:-9999px;width:0;height:0;border:0;';
    form.appendChild(frame);
    form.target = frame.name;

    var pending = false;
    var timer = null;

    function setStatus(msg, isError) {
      if (!statusWrap || !statusText) return;
      statusWrap.classList.toggle('ff-is-error', !!isError);
      statusWrap.hidden = false;
      // Write text after the live region is in the a11y tree so it's announced.
      statusText.textContent = msg;
    }
    function finish(ok) {
      if (!pending) return;
      pending = false;
      if (timer) { clearTimeout(timer); timer = null; }
      if (button) {
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
      }
      setStatus(ok ? successMsg : errorMsg, !ok);
      if (ok && input) input.value = '';
    }

    form.addEventListener('submit', function (e) {
      // HTML5 validation gates this event. Block re-submits while in flight.
      if (pending) { e.preventDefault(); return; }
      pending = true;
      if (statusWrap) statusWrap.hidden = true;
      if (button) {
        // Spinner via class only. We deliberately do NOT set button.disabled
        // here: Chrome cancels a form's native submission when its submit
        // button becomes disabled inside the submit handler, so the POST would
        // never fire. CSS .is-loading already sets pointer-events:none, which
        // blocks re-clicks; the `pending` guard above blocks Enter re-submits.
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      }
      // Safety net: the spinner must never hang forever if the response that
      // would fire the iframe's load event never arrives.
      timer = setTimeout(function () { finish(false); }, 12000);
    });

    frame.addEventListener('load', function () {
      if (!pending) return; // ignore the iframe's initial blank load
      var ok = true;
      try {
        // Same-origin (deployed theme): the redirect URL carries the result.
        ok = frame.contentWindow.location.href
          .indexOf('customer_posted=true') !== -1;
      } catch (e) {
        // Cross-origin (e.g. `shopify theme dev` posts to the live domain):
        // the iframe URL can't be read — the submit completed, assume success.
        ok = true;
      }
      finish(ok);
    });
  }

  /* ---------- Scroll-scrubbed reveal (design IX3 port) ---------- */
  var REVEAL = {
    duration: 2.5,   // per-word timeline length (relative units)
    stagger: 0.2,    // delay between words
    rise: 8,         // px, y offset at progress 0
    start: 0.9,      // timeline 0 when element top hits 90% of viewport
    end: 0.4,        // timeline 1 when element top hits 40% of viewport
    scrub: 0.8       // seconds of catch-up smoothing
  };
  var revealItems = [];
  var revealRafId = null;
  var revealLastTs = 0;
  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Wrap every word in a .rpw-word span, recursing into inline markup
     (<em>/<span> accents from inline_richtext) so styling survives */
  function splitWords(el, out) {
    Array.prototype.slice.call(el.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            var w = document.createElement('span');
            w.className = 'rpw-word';
            w.textContent = part;
            frag.appendChild(w);
            out.push(w);
          }
        });
        el.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        splitWords(child, out);
      }
    });
    return out;
  }

  function revealProgress(el) {
    var top = el.getBoundingClientRect().top;
    var vh = window.innerHeight;
    var p = (vh * REVEAL.start - top) / (vh * (REVEAL.start - REVEAL.end));
    return Math.min(1, Math.max(0, p));
  }

  function renderReveal(item, p) {
    var total = REVEAL.duration + REVEAL.stagger * (item.words.length - 1);
    for (var i = 0; i < item.words.length; i++) {
      var wp = (p * total - REVEAL.stagger * i) / REVEAL.duration;
      wp = Math.min(1, Math.max(0, wp));
      var eased = 1 - Math.pow(1 - wp, 2); // ~power1.out, design ease 2
      item.words[i].style.opacity = eased;
      item.words[i].style.transform =
        'translateY(' + REVEAL.rise * (1 - eased) + 'px)';
    }
  }

  function revealTick(ts) {
    var dt = revealLastTs ? Math.min(0.1, (ts - revealLastTs) / 1000) : 0.016;
    revealLastTs = ts;
    var settled = true;
    revealItems.forEach(function (item) {
      if (!item.el.isConnected) return;
      var diff = item.target - item.current;
      if (Math.abs(diff) < 0.001) {
        if (item.current !== item.target) {
          item.current = item.target;
          renderReveal(item, item.current);
        }
        return;
      }
      settled = false;
      item.current += diff * Math.min(1, dt / REVEAL.scrub);
      renderReveal(item, item.current);
    });
    if (settled) {
      revealRafId = null;
      revealLastTs = 0;
    } else {
      revealRafId = requestAnimationFrame(revealTick);
    }
  }

  function updateRevealTargets() {
    revealItems.forEach(function (item) {
      if (!item.el.isConnected) return;
      item.target = revealProgress(item.el);
    });
    if (!revealRafId) revealRafId = requestAnimationFrame(revealTick);
  }

  function initReveal(el) {
    if (el.dataset.rpwInit) return;
    el.dataset.rpwInit = '1';
    if (reduceMotion) return; // leave fully visible, no motion

    // Headlines get the design's word split; CTAs/cards rise as a unit
    var isHeadline = /(^|\s)ff-headline-/.test(el.className);
    var words = isHeadline ? splitWords(el, []) : [el];
    if (!words.length) words = [el];

    var item = { el: el, words: words, target: revealProgress(el), current: 0 };
    // First paint reflects the current scroll position (scrub semantics):
    // content already past the end line starts fully revealed
    item.current = item.target;
    renderReveal(item, item.current);
    revealItems.push(item);
  }

  function initReveals(scope) {
    (scope || document).querySelectorAll('[data-rpw-reveal]').forEach(initReveal);
    updateRevealTargets();
  }

  window.addEventListener('scroll', updateRevealTargets, { passive: true });
  window.addEventListener('resize', updateRevealTargets);

  function initAll() {
    document.querySelectorAll('.ff-carousel-parent, .Header--spacingMedium .Header__MainMenu, .MegaTabs__Nav, .rp-mdrawer__tabs').forEach(initDragScroll);
    document.querySelectorAll('[data-rpw-prev], [data-rpw-next]').forEach(initArrow);
    document.querySelectorAll('form.ff-newsletter-form').forEach(initNewsletter);
    initReveals(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init inside the theme editor when sections re-render */
  document.addEventListener('shopify:section:load', initAll);

  /* Exposed so AJAX-injected content (e.g. the PDP product-recommendations / recently-viewed
     carousels, whose track is swapped in after fetch) can re-bind drag-scroll. initAll is
     idempotent (each element is guarded by a dataset flag), so calling it again is safe. */
  window.rpwInitAll = initAll;
})();
